import { LoadingSpinner } from '@/components/LoadingSpinner'
import { MatchesList } from '@/components/MatchesList'
import { BracketView } from '@/components/knockout/BracketView'
import { BracketSetup } from '@/components/knockout/BracketSetup'
import { GroupStageSetup } from '@/components/group/GroupStageSetup'
import { GroupStageView } from '@/components/group/GroupStageView'
import { ReportGroupStageMatchDialog } from '@/components/group/ReportGroupStageMatchDialog'
import { ReportKnockoutMatchDialog } from '@/components/knockout/ReportKnockoutMatchDialog'
import { TournamentMatchEditDialog } from '@/components/knockout/TournamentMatchEditDialog'
import { TournamentPodium } from '@/components/knockout/TournamentPodium'
import { TournamentMatchHistory } from '@/components/knockout/TournamentMatchHistory'
import { CurrentBracketStandings } from '@/components/knockout/CurrentBracketStandings'
import { ReportMatchDialog } from '@/components/ReportMatchDialog'
import { SeriesRulesDialog } from '@/components/SeriesRulesDialog'
import { LeaderboardTable, normalizeLeaderboard, type UILBRow } from '@/components/LeaderboardTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { apiClient, handleApiError } from '@/services/api'
import type {
  GroupStage,
  GroupStageMatch,
  KnockoutBracket,
  KnockoutMatch,
  Player,
  ReportGroupStageMatchResponse,
  ReportKnockoutMatchResponse,
  Series,
  SeriesVisibility
} from '@/types/api'
import { Icon, ICONS } from '@/lib/icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { sportTranslationKey, seriesFormatTranslationKey } from '@/lib/sports'
import { useDebounce } from '@/hooks/useDebounce'
import { useSeriesPermissions } from '@/hooks/useSeriesPermissions'
import type { ReportedMatch } from '@/hooks/useMatchReporting'

const getDefaultTab = (
  series: Series | null,
  bracket: KnockoutBracket | null,
  groupStage: GroupStage | null
) => {
  if (!series) {
    return 'overview'
  }

  if (series.state === 'SERIES_STATE_COMPLETED') {
    return 'overview'
  }

  if (series.format === 'SERIES_FORMAT_CUP') {
    if (
      bracket?.state === 'KNOCKOUT_BRACKET_STATE_ACTIVE' ||
      bracket?.state === 'KNOCKOUT_BRACKET_STATE_COMPLETED'
    ) {
      return 'knockout'
    }
    return 'overview'
  }

  if (series.format === 'SERIES_FORMAT_GROUP_KNOCKOUT' || series.hasGroupStage) {
    if (
      bracket?.state === 'KNOCKOUT_BRACKET_STATE_ACTIVE' ||
      bracket?.state === 'KNOCKOUT_BRACKET_STATE_COMPLETED'
    ) {
      return 'knockout'
    }
    if (
      groupStage?.state === 'GROUP_STAGE_STATE_ACTIVE' ||
      groupStage?.state === 'GROUP_STAGE_STATE_COMPLETED'
    ) {
      return 'groups'
    }
    return 'overview'
  }

  // For all other series formats (OPEN, LADDER), default to overview tab
  return 'overview'
}

export function SeriesDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [series, setSeries] = useState<Series | null>(null)
  const permissions = useSeriesPermissions(series)
  const [clubName, setClubName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [showReportDialog, setShowReportDialog] = useState(false)
  const [showRulesDialog, setShowRulesDialog] = useState(false)
  const [matchesRefreshKey, setMatchesRefreshKey] = useState(0)
  const [bracket, setBracket] = useState<KnockoutBracket | null>(null)
  const [loadingBracket, setLoadingBracket] = useState(false)
  const [bracketError, setBracketError] = useState<string | null>(null)
  const [playersById, setPlayersById] = useState<Record<string, Player>>({})
  const [groupStage, setGroupStage] = useState<GroupStage | null>(null)
  const [loadingGroupStage, setLoadingGroupStage] = useState(false)
  const [groupStageError, setGroupStageError] = useState<string | null>(null)
  const playersCacheRef = useRef<Map<string, Player>>(new Map())
  const [activeBracketMatch, setActiveBracketMatch] = useState<KnockoutMatch | null>(null)
  const [showKnockoutReport, setShowKnockoutReport] = useState(false)
  const [activeGroupMatch, setActiveGroupMatch] = useState<GroupStageMatch | null>(null)
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null)
  const [showGroupReport, setShowGroupReport] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingMatch, setEditingMatch] = useState<{ match: KnockoutMatch | GroupStageMatch; groupId?: string; isGroupStage: boolean } | null>(null)
  const [advanceLoading, setAdvanceLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('')
  const [showCompleteDialog, setShowCompleteDialog] = useState(false)
  const [completing, setCompleting] = useState(false)
  
  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState<UILBRow[]>([])
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false)

  const loadSeries = useCallback(async (seriesId: string) => {
    try {
      setLoading(true)
      const seriesData = await apiClient.getSeries(seriesId)
      setSeries(seriesData)

      // Load club name if clubId is present
      if (seriesData.clubId) {
        loadClubName(seriesData.clubId)
      }
    } catch (error: unknown) {
      handleApiError(error, (apiError) => {
        // If getSeries is not implemented, show a helpful error
        if (apiError.message?.includes('not implemented')) {
          toast.error('Series details endpoint not yet implemented in backend')
        } else {
          toast.error(apiError.message || t('errors.generic'))
        }
      })
    } finally {
      setLoading(false)
    }
  }, [t])

  const loadLeaderboard = useCallback(async (seriesId: string) => {
    try {
      setLoadingLeaderboard(true)
      const resp = await apiClient.getLeaderboard({ seriesId, pageSize: 50 }, 'leaderboard')
      setLeaderboard(normalizeLeaderboard(resp))
    } catch (_error: unknown) {
      // Silently fail for leaderboard - it's not critical
      setLeaderboard([])
    } finally {
      setLoadingLeaderboard(false)
    }
  }, [])

  const ensurePlayers = useCallback(async (ids: string[]) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)))
    if (uniqueIds.length === 0) {
      return
    }

    const missingIds = uniqueIds.filter(id => !playersCacheRef.current.has(id))
    if (missingIds.length === 0) {
      return
    }

    // Fetch players in batches with delay to avoid rate limiting
    const batchSize = 3
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
    const results: PromiseSettledResult<Player | null>[] = []
    
    for (let i = 0; i < missingIds.length; i += batchSize) {
      const batch = missingIds.slice(i, i + batchSize)
      const batchResults = await Promise.allSettled(
        batch.map(async playerId => {
          try {
            return await apiClient.getPlayer(playerId)
          } catch (error) {
            handleApiError(error, () => {
              /* ignore individual player errors */
            })
            return null
          }
        })
      )
      results.push(...batchResults)
      
      // Add delay between batches (except for the last batch)
      if (i + batchSize < missingIds.length) {
        await delay(100)
      }
    }

    const playersToAdd: Player[] = []
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        playersToAdd.push(result.value)
      }
    }

    if (playersToAdd.length > 0) {
      setPlayersById(prev => {
        const next = { ...prev }
        for (const player of playersToAdd) {
          playersCacheRef.current.set(player.id, player)
          next[player.id] = player
        }
        return next
      })
    }
  }, [])

  const buildFallbackPlayer = useCallback(
    (id: string, fallbackName?: string): Player => ({
      id,
      displayName:
        fallbackName && fallbackName.trim().length > 0
          ? fallbackName
          : t('leaderboard.unknownPlayer', 'Unknown player'),
      active: true,
      email: '',
      firstName: '',
      lastName: '',
      clubMemberships: [],
      isPlatformOwner: false,
      createdAt: new Date(0).toISOString(),
      statsBySport: {},
    }),
    [t],
  )

  const collectBracketPlayerIds = useCallback((matches: KnockoutMatch[]) => {
    const ids = new Set<string>()
    for (const match of matches) {
      if (match.playerAId) ids.add(match.playerAId)
      if (match.playerBId) ids.add(match.playerBId)
      if (match.winnerId) ids.add(match.winnerId)
    }
    return Array.from(ids)
  }, [])

  const collectGroupStagePlayerIds = useCallback((stageData: GroupStage) => {
    const ids = new Set<string>()
    stageData.groups.forEach(group => {
      group.playerIds.forEach(id => ids.add(id))
      group.matches.forEach(match => {
        if (match.playerAId) ids.add(match.playerAId)
        if (match.playerBId) ids.add(match.playerBId)
        if (match.winnerId) ids.add(match.winnerId)
      })
    })
    return Array.from(ids)
  }, [])

  const loadKnockoutBracket = useCallback(async (seriesId: string) => {
    try {
      setLoadingBracket(true)
      setBracketError(null)
      const response = await apiClient.getKnockoutBracket(seriesId)
      const bracketData = response.bracket ?? null
      setBracket(bracketData)
      if (bracketData) {
        ensurePlayers(collectBracketPlayerIds(bracketData.matches))
      }
    } catch (error: unknown) {
      handleApiError(error, (apiError) => {
        if (apiError.code === 'BRACKET_NOT_FOUND') {
          setBracket(null)
          setBracketError(null)
          return
        }
        const message = apiError.message || t('errors.generic')
        setBracketError(message)
        toast.error(message)
      })
    } finally {
      setLoadingBracket(false)
    }
  }, [collectBracketPlayerIds, ensurePlayers, t])

  const loadGroupStageData = useCallback(
    async (seriesId: string) => {
      try {
        setLoadingGroupStage(true)
        setGroupStageError(null)
        const response = await apiClient.getGroupStage(seriesId)
        const stageData = response.groupStage ?? null
        setGroupStage(stageData)

        if (stageData) {
          ensurePlayers(collectGroupStagePlayerIds(stageData))
        }

        const bracketData = response.bracket ?? null
        setBracket(bracketData)
        if (bracketData) {
          ensurePlayers(collectBracketPlayerIds(bracketData.matches))
        }
      } catch (error: unknown) {
        handleApiError(error, apiError => {
          if (apiError.code === 'GROUP_STAGE_NOT_FOUND') {
            setGroupStage(null)
            setGroupStageError(null)
            setBracket(null)
            setBracketError(null)
            return
          }
          const message = apiError.message || t('errors.generic')
          setGroupStageError(message)
          toast.error(message)
        })
      } finally {
        setLoadingGroupStage(false)
      }
    },
    [collectBracketPlayerIds, collectGroupStagePlayerIds, ensurePlayers, t]
  )

  useEffect(() => {
    if (id) {
      loadSeries(id)
    }
  }, [id, loadSeries])

  useEffect(() => {
    playersCacheRef.current.clear()
    setPlayersById({})
  }, [id])

  // Load leaderboard when series is loaded
  useEffect(() => {
    if (series?.id) {
      loadLeaderboard(series.id)
    }
  }, [series?.id, loadLeaderboard])

  useEffect(() => {
    if (!series || series.state !== 'SERIES_STATE_COMPLETED') {
      return
    }

    const placements = series.podium?.placements ?? []
    const ids = placements.flatMap(placement => placement.playerIds ?? []).filter((id): id is string => Boolean(id))

    if (ids.length > 0) {
      ensurePlayers(ids).catch(() => {
        // Player fetch failures are already handled inside ensurePlayers
      })
    }
  }, [ensurePlayers, series])

  useEffect(() => {
    if (!series?.id) {
      setBracket(null)
      setGroupStage(null)
      setBracketError(null)
      setGroupStageError(null)
      return
    }

    const includesGroupStage = series.format === 'SERIES_FORMAT_GROUP_KNOCKOUT' || Boolean(series.hasGroupStage)
    const includesKnockout = series.format === 'SERIES_FORMAT_CUP' || includesGroupStage

    if (includesGroupStage) {
      loadGroupStageData(series.id)
    } else if (includesKnockout) {
      loadKnockoutBracket(series.id)
    } else {
      setBracket(null)
      setGroupStage(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series?.id, series?.format, series?.hasGroupStage])

  const loadClubName = async (clubId: string) => {
    try {
      const club = await apiClient.getClub(clubId)
      setClubName(club.name)
    } catch (_error: unknown) {
      // Keep the ID as fallback if club name fetch fails
      setClubName(clubId)
    }
  }

  const handleCompleteSeries = async () => {
    if (!id) return

    try {
      setCompleting(true)
      const response = await apiClient.completeSeries(id)
      setSeries(response.series)
      setShowCompleteDialog(false)

      // Reload series data to get updated state
      await loadSeries(id)
      
      // Reload leaderboard to show final rankings
      if (response.series.state === 'SERIES_STATE_COMPLETED') {
        loadLeaderboard(id)
      }

      toast.success(t('series.complete_success_title'), {
        description: t('series.complete_success_description'),
        action: {
          label: t('series.view_podium_now'),
          onClick: () => {
            navigate(`/series/${id}/leaderboard`)
          },
        },
      })
    } catch (error: unknown) {
      handleApiError(error, (apiError) => {
        toast.error(apiError.message || t('series.complete_error'))
      })
    } finally {
      setCompleting(false)
    }
  }

  const getVisibilityLabel = (visibility: SeriesVisibility) => {
    return visibility === 'SERIES_VISIBILITY_OPEN' ? t('series.visibility.open') : t('series.visibility.club_only')
  }

  const getVisibilityVariant = (visibility: SeriesVisibility) => {
    return visibility === 'SERIES_VISIBILITY_OPEN' ? 'default' : 'secondary'
  }

  const formatDateRange = (startDate: string, endDate: string) => {
    try {
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Check if dates are valid
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return t('errors.invalidDates')
      }

      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`
    } catch (_error) {
      return t('errors.invalidDates')
    }
  }

  const getBracketStateLabel = useCallback((state: KnockoutBracket['state']) => {
    switch (state) {
      case 'KNOCKOUT_BRACKET_STATE_DRAFT':
        return t('knockout.state.draft', 'Draft')
      case 'KNOCKOUT_BRACKET_STATE_ACTIVE':
        return t('knockout.state.active', 'In progress')
      case 'KNOCKOUT_BRACKET_STATE_COMPLETED':
        return t('knockout.state.completed', 'Completed')
      default:
        return t('knockout.state.unspecified', 'Not started')
    }
  }, [t])

  const getBracketStateVariant = (state: KnockoutBracket['state']) => {
    switch (state) {
      case 'KNOCKOUT_BRACKET_STATE_COMPLETED':
        return 'default' as const
      case 'KNOCKOUT_BRACKET_STATE_ACTIVE':
        return 'secondary' as const
      default:
        return 'outline' as const
    }
  }

  const getLockedReasonMessage = useCallback(
    (reason?: string) => {
      const fallback = t('match.locked.fallback', 'This match cannot be edited right now.')
      if (!reason) {
        return fallback
      }
      return t(`match.locked.reasons.${reason}`, fallback)
    },
    [t]
  )

  // Debounce the refresh to avoid excessive API calls during rapid match entry
  const debouncedRefreshKey = useDebounce(matchesRefreshKey, 500)
  const [pendingRefresh, setPendingRefresh] = useState(false)

  // Memoize handlers to prevent unnecessary re-renders
  const handleMatchReported = useCallback((_reportedMatch: ReportedMatch) => {
    setPendingRefresh(true)
    setMatchesRefreshKey((prev) => prev + 1)
    // Also refresh leaderboard when a match is reported
    if (series?.id) {
      loadLeaderboard(series.id)
    }
  }, [series?.id, loadLeaderboard])

  const handleBracketCreated = useCallback((newBracket: KnockoutBracket) => {
    setBracketError(null)
    setBracket(newBracket)
    ensurePlayers(collectBracketPlayerIds(newBracket.matches))
    setActiveTab('knockout')
    toast.success(t('knockout.created', 'Knockout bracket created'))
  }, [collectBracketPlayerIds, ensurePlayers, t])

  const handleKnockoutReportRequested = useCallback(
    async (match: KnockoutMatch) => {
      if (!series?.id) {
        return
      }

      if (match.isLocked) {
        toast.error(getLockedReasonMessage(match.lockedReason))
        return
      }

      try {
        const response = await apiClient.checkKnockoutMatchEditability(series.id, match.id)
        if (!response.isEditable) {
          toast.error(getLockedReasonMessage(response.reason))
          return
        }
      } catch (error: unknown) {
        handleApiError(error, apiError => {
          toast.error(apiError.message || t('errors.generic'))
        })
        return
      }

      setActiveBracketMatch(match)
      setShowKnockoutReport(true)
    },
    [getLockedReasonMessage, series?.id, t]
  )

  const handleKnockoutDialogOpenChange = useCallback((open: boolean) => {
    setShowKnockoutReport(open)
    if (!open) {
      setActiveBracketMatch(null)
    }
  }, [])

  const handleKnockoutMatchReported = useCallback(async (response: ReportKnockoutMatchResponse) => {
    const updatedBracket = response.bracket
    setBracket(updatedBracket)
    ensurePlayers(collectBracketPlayerIds(updatedBracket.matches))
    setMatchesRefreshKey((prev) => prev + 1)
    if (series?.id) {
      loadLeaderboard(series.id)
    }
  }, [collectBracketPlayerIds, ensurePlayers, loadLeaderboard, series?.id])

  const handleGroupStageCreated = useCallback(
    (payload: { stage: GroupStage; bracket: KnockoutBracket }) => {
      setGroupStage(payload.stage)
      setBracket(payload.bracket)
      setGroupStageError(null)
      setBracketError(null)
      ensurePlayers([
        ...collectGroupStagePlayerIds(payload.stage),
        ...collectBracketPlayerIds(payload.bracket.matches)
      ])
      setActiveTab('groups')
      toast.success(t('groupStage.created', 'Group stage created'))
    },
    [collectBracketPlayerIds, collectGroupStagePlayerIds, ensurePlayers, t]
  )

  const handleGroupStageReportRequested = useCallback(
    (groupId: string, match: GroupStageMatch) => {
      if (match.isLocked) {
        toast.error(getLockedReasonMessage(match.lockedReason))
        return
      }
      setActiveGroupId(groupId)
      setActiveGroupMatch(match)
      setShowGroupReport(true)
    },
    [getLockedReasonMessage]
  )

  const handleGroupDialogOpenChange = useCallback((open: boolean) => {
    setShowGroupReport(open)
    if (!open) {
      setActiveGroupId(null)
      setActiveGroupMatch(null)
    }
  }, [])

  const handleGroupStageMatchReported = useCallback(
    (response: ReportGroupStageMatchResponse) => {
      setGroupStage(response.groupStage)
      ensurePlayers(collectGroupStagePlayerIds(response.groupStage))

      if (response.bracket) {
        setBracket(response.bracket)
        ensurePlayers(collectBracketPlayerIds(response.bracket.matches))
      }

      setMatchesRefreshKey(prev => prev + 1)
      if (series?.id) {
        loadLeaderboard(series.id)
      }
    },
    [collectBracketPlayerIds, collectGroupStagePlayerIds, ensurePlayers, loadLeaderboard, series?.id]
  )

  const handleAdvanceGroupStage = useCallback(async () => {
    if (!series?.id) {
      return
    }

    try {
      setAdvanceLoading(true)
      const response = await apiClient.advanceGroupStage(series.id)
      setGroupStage(response.groupStage)
      setBracket(response.bracket)
      ensurePlayers([
        ...collectGroupStagePlayerIds(response.groupStage),
        ...collectBracketPlayerIds(response.bracket.matches)
      ])
      toast.success(t('groupStage.view.advanceSuccess', 'Knockout bracket generated'))
      setActiveTab('knockout')
      setMatchesRefreshKey(prev => prev + 1)
      loadLeaderboard(series.id)
    } catch (error: unknown) {
      handleApiError(error, apiError => {
        toast.error(apiError.message || t('errors.generic'))
      })
    } finally {
      setAdvanceLoading(false)
    }
  }, [
    collectBracketPlayerIds,
    collectGroupStagePlayerIds,
    ensurePlayers,
    loadLeaderboard,
    series?.id,
    t
  ])

  const handleGroupStageRefresh = useCallback(() => {
    if (series?.id) {
      loadGroupStageData(series.id)
    }
  }, [loadGroupStageData, series?.id])

  // Handle editing a knockout match
  const handleEditKnockoutMatch = useCallback((match: KnockoutMatch) => {
    if (match.isLocked) {
      toast.error(getLockedReasonMessage(match.lockedReason))
      return
    }
    setEditingMatch({ match, isGroupStage: false })
    setShowEditDialog(true)
  }, [getLockedReasonMessage])

  // Handle editing a group stage match
  const handleEditGroupStageMatch = useCallback((groupId: string, match: GroupStageMatch) => {
    if (match.isLocked) {
      toast.error(getLockedReasonMessage(match.lockedReason))
      return
    }
    setEditingMatch({ match, groupId, isGroupStage: true })
    setShowEditDialog(true)
  }, [getLockedReasonMessage])

  // Handle match edit completion
  const handleMatchEdited = useCallback(() => {
    setShowEditDialog(false)
    setEditingMatch(null)
    // Refresh the tournament data
    if (series?.id) {
      loadKnockoutBracket(series.id)
      loadGroupStageData(series.id)
      loadLeaderboard(series.id)
    }
    setMatchesRefreshKey(prev => prev + 1)
  }, [loadGroupStageData, loadKnockoutBracket, loadLeaderboard, series?.id])

  // Handle completing group stage
  const handleCompleteGroupStage = useCallback(async () => {
    if (!series?.id) {
      return
    }

    try {
      const response = await apiClient.completeGroupStage(series.id)
      setGroupStage(response.groupStage)
      if (response.bracket) {
        setBracket(response.bracket)
      }
      toast.success(t('tournament.complete.groupStage.success'))
      setMatchesRefreshKey(prev => prev + 1)
    } catch (error: unknown) {
      handleApiError(error, apiError => {
        toast.error(apiError.message || t('errors.generic'))
      })
    }
  }, [series?.id, t])

  // Handle completing bracket
  const handleCompleteBracket = useCallback(async () => {
    if (!bracket?.id) {
      return
    }

    try {
      const response = await apiClient.completeBracket(bracket.id)
      setBracket(response.bracket)
      toast.success(t('tournament.complete.bracket.success'))
      setMatchesRefreshKey(prev => prev + 1)
      // Navigate to leaderboard tab to show podium
      setActiveTab('leaderboard')
    } catch (error: unknown) {
      handleApiError(error, apiError => {
        toast.error(apiError.message || t('errors.generic'))
      })
    }
  }, [bracket?.id, t])

  // Extract podium standings for completed knockout tournaments
  const completedSeriesPodium = useMemo(() => {
    if (!series || series.state !== 'SERIES_STATE_COMPLETED' || !series.podium) {
      return null
    }

    const placements = series.podium.placements ?? []
    const getPlacement = (position: number) => placements.find(placement => placement.placement === position)

    const championPlacement = getPlacement(1)
    const runnerUpPlacement = getPlacement(2)

    if (!championPlacement || !runnerUpPlacement) {
      return null
    }

    const toPodiumEntry = (placement: NonNullable<Series['podium']>['placements'][number]) => {
      const players: Player[] =
        placement.playerIds && placement.playerIds.length > 0
          ? placement.playerIds.map((playerId, index) => {
              if (!playerId) {
                return buildFallbackPlayer(`placement-${placement.placement}-${index}`, placement.playerName)
              }
              return playersById[playerId] ?? buildFallbackPlayer(playerId, placement.playerName)
            })
          : [buildFallbackPlayer(`placement-${placement.placement}-fallback`, placement.playerName)]

      const wins = placement.stats?.wins ?? 0
      const losses = placement.stats?.losses ?? 0
      const matchesPlayed = wins + losses
      const winRate = matchesPlayed > 0 ? Math.round((wins / matchesPlayed) * 100) : undefined

      return {
        players,
        matchesPlayed,
        wins,
        losses,
        winRate,
        extraStats: {
          setDifference: placement.stats?.setDifference,
          eloRating: placement.stats?.eloRating,
          ladderPosition: placement.stats?.ladderPosition,
        },
      }
    }

    const champion = toPodiumEntry(championPlacement)
    const runnerUp = toPodiumEntry(runnerUpPlacement)
    const thirdPlacement = getPlacement(3)
    const thirdPlace = thirdPlacement ? toPodiumEntry(thirdPlacement) : undefined

    return { champion, runnerUp, thirdPlace }
  }, [buildFallbackPlayer, playersById, series])

  // Clear pending state when debounced refresh completes
  useEffect(() => {
    if (pendingRefresh && debouncedRefreshKey > 0) {
      setPendingRefresh(false)
    }
  }, [debouncedRefreshKey, pendingRefresh])

  const includesGroupStage =
    series?.format === 'SERIES_FORMAT_GROUP_KNOCKOUT' || Boolean(series?.hasGroupStage)
  const isKnockoutSeries = series?.format === 'SERIES_FORMAT_CUP' || includesGroupStage

  const defaultTab = useMemo(() => getDefaultTab(series, bracket, groupStage), [bracket, groupStage, series])

  const shouldShowTab = useCallback(
    (tab: 'overview' | 'groups' | 'knockout' | 'leaderboard') => {
      switch (tab) {
        case 'groups':
          return includesGroupStage
        case 'knockout':
          if (series?.format === 'SERIES_FORMAT_CUP') {
            return true
          }
          return Boolean(bracket) || groupStage?.state === 'GROUP_STAGE_STATE_COMPLETED'
        case 'leaderboard':
        case 'overview':
        default:
          return true
      }
    },
    [bracket, groupStage?.state, includesGroupStage, series?.format]
  )

  useEffect(() => {
    if (!series) {
      return
    }

    if (!activeTab) {
      setActiveTab(defaultTab)
      return
    }

    if (!shouldShowTab(activeTab as 'overview' | 'groups' | 'knockout' | 'leaderboard')) {
      setActiveTab(defaultTab)
    }
  }, [activeTab, defaultTab, series, shouldShowTab])

  const stateMessage = useMemo(() => {
    if (!series) {
      return null
    }

    if (series.state === 'SERIES_STATE_COMPLETED') {
      return {
        icon: ICONS.trophy,
        message: t('series.messages.completed'),
        variant: 'success' as const,
      }
    }

    if (bracket?.state === 'KNOCKOUT_BRACKET_STATE_COMPLETED') {
      return {
        icon: ICONS.check,
        message: t('series.messages.ready_to_complete'),
        variant: 'success' as const,
      }
    }

    if (bracket?.state === 'KNOCKOUT_BRACKET_STATE_ACTIVE') {
      return {
        icon: ICONS.tournament,
        message: t('series.messages.knockout_active'),
        variant: 'info' as const,
      }
    }

    if (groupStage?.state === 'GROUP_STAGE_STATE_COMPLETED') {
      return {
        icon: ICONS.checkCircle,
        message: t('series.messages.group_completed_advance_ready'),
        variant: 'warning' as const,
      }
    }

    if (groupStage?.state === 'GROUP_STAGE_STATE_ACTIVE') {
      return {
        icon: ICONS.tournament,
        message: t('series.messages.awaiting_matches'),
        variant: 'info' as const,
      }
    }

    return {
      icon: ICONS.clock,
      message: t('series.messages.awaiting_matches'),
      variant: 'info' as const,
    }
  }, [bracket?.state, groupStage?.state, series, t])

  if (loading) {
    return <LoadingSpinner />
  }

  if (!series) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-foreground mb-2">Series not found</h2>
        <p className="text-muted-foreground mb-4">The series you're looking for doesn't exist.</p>
        <Link to="/">
          <Button>
            <Icon icon={ICONS.arrowLeft} width={16} height={16} />
            <span className="ml-2">Back to Series</span>
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/">
          <Button variant="ghost" size="sm">
            <Icon icon={ICONS.arrowLeft} width={16} height={16} className="text-muted-foreground" />
            <span className="ml-2">{t('common.back')}</span>
          </Button>
        </Link>
      </div>

      {/* Series Info */}
      <Card>
        <CardHeader>
          <div className="flex flex-col space-y-4">
            <div className="space-y-2 flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-3">
                <CardTitle className="text-2xl">{series.title}</CardTitle>
                <Badge variant={getVisibilityVariant(series.visibility)}>
                  {getVisibilityLabel(series.visibility)}
                </Badge>
              </div>
              <CardDescription className="text-base">
                {series.visibility === 'SERIES_VISIBILITY_OPEN' ? t('series.visibility.open_description') : t('series.visibility.club_only_description')}
              </CardDescription>
            </div>
            {/* Action Buttons - Mobile Optimized */}
            <div className="flex flex-wrap gap-2">
              {!isKnockoutSeries && (
                <Button
                  onClick={() => setShowReportDialog(true)}
                  className="flex-1 sm:flex-initial whitespace-nowrap"
                  size="sm"
                  disabled={!permissions.canReportMatches}
                  title={permissions.completionBlockMessage || undefined}
                >
                  <Icon icon={ICONS.add} width={16} height={16} className="text-current" />
                  <span className="ml-2">{t('series.report.match')}</span>
                </Button>
              )}
              <Button
                onClick={() => setShowRulesDialog(true)}
                variant="outline"
                className="flex-1 sm:flex-initial whitespace-nowrap"
                size="sm"
              >
                <Icon icon={ICONS.info} width={16} height={16} className="text-info" />
                <span className="ml-2">{t('series.rules.viewRules')}</span>
              </Button>
              {/* Only show Podium button when series is completed (any type: open, ladder, cup, group+knockout) */}
              {series.state === 'SERIES_STATE_COMPLETED' && (
                <Link to={`/series/${series.id}/leaderboard`} className="flex-1 sm:flex-initial">
                  <Button 
                    variant="outline"
                    className="w-full whitespace-nowrap"
                    size="sm"
                  >
                    <Icon icon={ICONS.podium} width={16} height={16} className="text-info" />
                    <span className="ml-2">{t('series.leaderboard')}</span>
                  </Button>
                </Link>
              )}
              {/* Show "Avsluta serie" button only when there's content to complete:
                  - For OPEN series: only when matches have been reported (leaderboard has entries)
                  - For knockout series: when bracket is completed
                  - Always requires admin permissions
              */}
              {permissions.canCompleteSeries && (
                (isKnockoutSeries && bracket?.state === 'KNOCKOUT_BRACKET_STATE_COMPLETED') ||
                (!isKnockoutSeries && leaderboard.length > 0)
              ) && (
                <Button
                  onClick={() => setShowCompleteDialog(true)}
                  variant="default"
                  className="flex-1 sm:flex-initial whitespace-nowrap"
                  size="sm"
                >
                  <Icon icon={ICONS.tickCircle} width={16} height={16} className="text-current" />
                  <span className="ml-2">{t('series.complete')}</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center text-sm">
                <Icon icon={ICONS.calendar} width={16} height={16} className="mr-2 text-muted-foreground" />
                <span className="text-muted-foreground mr-2">Duration:</span>
                <span className="font-medium">
                  {formatDateRange(series.startsAt, series.endsAt)}
                </span>
              </div>
              <div className="flex items-center text-sm">
                <Icon icon={ICONS.tournament} width={16} height={16} className="mr-2 text-muted-foreground" />
                <span className="text-muted-foreground mr-2">{t('series.sportLabel')}:</span>
                <span className="font-medium">{t(sportTranslationKey(series.sport))}</span>
              </div>
              <div className="flex items-center text-sm">
                <Icon icon={ICONS.clipboard} width={16} height={16} className="mr-2 text-muted-foreground" />
                <span className="text-muted-foreground mr-2">{t('series.formatLabel')}:</span>
                <span className="font-medium">{t(seriesFormatTranslationKey(series.format))}</span>
              </div>
              {series.clubId && (
                <div className="flex items-center text-sm">
                  <Icon icon={ICONS.club} width={16} height={16} className="mr-2 text-muted-foreground" />
                  <span className="text-muted-foreground mr-2">{t('players.club')}:</span>
                  <span className="font-medium">{clubName || series.clubId}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {stateMessage && (
      <Alert variant={stateMessage.variant} className="flex items-start gap-3">
        <Icon icon={stateMessage.icon} width={20} height={20} className="mt-0.5 text-current" />
        <AlertDescription className="text-current">{stateMessage.message}</AlertDescription>
      </Alert>
    )}

      {series.state === 'SERIES_STATE_COMPLETED' && (
        <div className="space-y-4" data-testid="series-completed-banner">
          <Badge variant="secondary" className="inline-flex items-center gap-2">
            <Icon icon={ICONS.podium} width={14} height={14} />
            {t('series.viewingCompletedSeries', 'Viewing completed series')}
          </Badge>
          {series.podium?.completedAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon icon={ICONS.calendar} width={16} height={16} />
              <span>
                {t('series.seriesCompleted', {
                  date: new Date(series.podium.completedAt).toLocaleDateString(),
                })}
              </span>
            </div>
          )}
          {completedSeriesPodium ? (
            <TournamentPodium
              champion={completedSeriesPodium.champion}
              runnerUp={completedSeriesPodium.runnerUp}
              thirdPlace={completedSeriesPodium.thirdPlace}
              seriesTitle={series.title}
              completedAt={series.podium?.completedAt}
            />
          ) : (
            <Alert>
              <AlertTitle>{t('leaderboard.podiumPendingTitle')}</AlertTitle>
              <AlertDescription>{t('leaderboard.podiumPendingDescription')}</AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Content Tabs */}
      <Tabs value={activeTab || defaultTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          {shouldShowTab('overview') && (
            <TabsTrigger value="overview">{t('series.overviewTab', 'Overview')}</TabsTrigger>
          )}
          {shouldShowTab('groups') && (
            <TabsTrigger value="groups">{t('groupStage.tabs.groups', 'Group stage')}</TabsTrigger>
          )}
          {shouldShowTab('knockout') && (
            <TabsTrigger value="knockout">{t('knockout.bracketTab', 'Bracket')}</TabsTrigger>
          )}
          {shouldShowTab('leaderboard') && (
            <TabsTrigger value="leaderboard">{t('series.leaderboardTab', 'Results')}</TabsTrigger>
          )}
        </TabsList>

        {shouldShowTab('overview') && (
          <TabsContent value="overview" className="space-y-6">
            {!isKnockoutSeries && (
              <MatchesList
                seriesId={series.id}
                seriesStartDate={series.startsAt}
                seriesEndDate={series.endsAt}
                seriesName={series.title}
                refreshKey={debouncedRefreshKey}
                series={series}
              />
            )}

            {isKnockoutSeries && (
              <div className="space-y-4">
                {includesGroupStage ? (
                  <>
                    {groupStage ? (
                      <Alert>
                        <AlertTitle>{t('groupStage.setup.stageCreatedTitle', 'Group stage configured')}</AlertTitle>
                        <AlertDescription className="text-current">
                          {t(
                            'groupStage.setup.stageCreatedDescription',
                            'Manage matches from the group stage tab to update standings.'
                          )}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert>
                        <AlertTitle>{t('groupStage.setup.noStageTitle', 'No group stage yet')}</AlertTitle>
                        <AlertDescription className="text-current">
                          {t(
                            'groupStage.setup.noStageDescription',
                            'Use the overview tab to select players and create the groups for this tournament.'
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    {(!groupStage ||
                      ['GROUP_STAGE_STATE_UNSPECIFIED', 'GROUP_STAGE_STATE_PENDING'].includes(groupStage.state)) && (
                      <GroupStageSetup
                        seriesId={series.id}
                        seriesTitle={series.title}
                        seriesSport={series.sport}
                        clubId={series.visibility === 'SERIES_VISIBILITY_CLUB_ONLY' ? series.clubId : undefined}
                        onCreated={handleGroupStageCreated}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {bracket ? (
                      <Alert>
                        <AlertTitle>{t('knockout.setup.bracketCreatedTitle', 'Bracket created')}</AlertTitle>
                        <AlertDescription className="text-current">
                          {t(
                            'knockout.setup.bracketCreatedDescription',
                            'The bracket is ready. Manage matches from the knockout tab.'
                          )}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert>
                        <AlertTitle>{t('knockout.setup.noBracketTitle', 'No bracket yet')}</AlertTitle>
                        <AlertDescription className="text-current">
                          {t(
                            'knockout.setup.noBracketDescription',
                            'Use the overview tab to seed players and start the bracket.'
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    {!bracket && (
                      <BracketSetup
                        seriesId={series.id}
                        seriesTitle={series.title}
                        seriesSport={series.sport}
                        clubId={series.visibility === 'SERIES_VISIBILITY_CLUB_ONLY' ? series.clubId : undefined}
                        onCreated={handleBracketCreated}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </TabsContent>
        )}

        {shouldShowTab('groups') && (
          <TabsContent value="groups" className="space-y-6">
            {groupStageError && (
              <Alert variant="destructive">
                <AlertTitle>{t('groupStage.view.errorTitle', 'Unable to load group stage')}</AlertTitle>
                <AlertDescription>{groupStageError}</AlertDescription>
              </Alert>
            )}

            {loadingGroupStage ? (
              <div className="flex justify-center py-10">
                <LoadingSpinner />
              </div>
            ) : groupStage ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm text-muted-foreground">
                    {t('groupStage.view.lastUpdated', 'Standings update automatically as you report results.')}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleGroupStageRefresh}>
                      <Icon icon={ICONS.refresh} width={16} height={16} />
                      <span className="ml-2">{t('groupStage.view.refresh', 'Refresh')}</span>
                    </Button>
                  </div>
                </div>
                <GroupStageView
                  stage={groupStage}
                  playersById={playersById}
                  sport={series.sport}
                  onReportMatch={handleGroupStageReportRequested}
                  onEditMatch={permissions.canEditMatches ? handleEditGroupStageMatch : undefined}
                  onAdvance={handleAdvanceGroupStage}
                  onCompleteGroupStage={handleCompleteGroupStage}
                  advanceLoading={advanceLoading}
                  bracketState={bracket?.state}
                />
              </div>
            ) : (
              <Alert>
                <AlertTitle>{t('groupStage.view.emptyTitle', 'Group stage not configured')}</AlertTitle>
                <AlertDescription className="text-current">
                  {t(
                    'groupStage.view.emptyDescription',
                    'Set up the group stage from the overview tab to start playing.'
                  )}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>
        )}

        {shouldShowTab('knockout') && (
          <TabsContent value="knockout" className="space-y-6">
            {bracketError && (
              <Alert variant="destructive">
                <AlertTitle>{t('knockout.error.title', 'Unable to load bracket')}</AlertTitle>
                <AlertDescription>{bracketError}</AlertDescription>
              </Alert>
            )}

            {loadingBracket ? (
              <div className="flex justify-center py-10">
                <LoadingSpinner />
              </div>
            ) : bracket ? (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1">
                    <h3 className="text-lg font-semibold text-foreground">
                      {t('knockout.bracketTitle', 'Knockout bracket')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('knockout.bracketSubtitle', 'Report results to advance players through the bracket.')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getBracketStateVariant(bracket.state)}>
                      {getBracketStateLabel(bracket.state)}
                    </Badge>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => loadKnockoutBracket(series.id)}
                    >
                      <Icon icon={ICONS.tournament} width={16} height={16} />
                      <span className="ml-2">{t('knockout.refresh', 'Refresh')}</span>
                    </Button>
                  </div>
                </div>

                <BracketView
                  bracket={bracket}
                  playersById={playersById}
                  onReportMatch={handleKnockoutReportRequested}
                  onEditMatch={permissions.canEditMatches ? handleEditKnockoutMatch : undefined}
                  onCompleteBracket={handleCompleteBracket}
                />
              </div>
            ) : includesGroupStage ? (
              <Alert>
                <AlertTitle>{t('groupStage.knockout.waitingTitle', 'Knockout bracket locked')}</AlertTitle>
                <AlertDescription>
                  {t(
                    'groupStage.knockout.waitingDescription',
                    'Finish all group matches and generate the bracket before reporting knockout results.'
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-foreground">
                    {t('knockout.setup.title', 'Create knockout bracket')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('knockout.setup.subtitle', 'Select players to create the bracket for this series.')}
                  </p>
                </div>
                <BracketSetup
                  seriesId={series.id}
                  seriesTitle={series.title}
                  seriesSport={series.sport}
                  clubId={series.visibility === 'SERIES_VISIBILITY_CLUB_ONLY' ? series.clubId : undefined}
                  onCreated={handleBracketCreated}
                />
              </div>
            )}
          </TabsContent>
        )}

        {shouldShowTab('leaderboard') && (
          <TabsContent value="leaderboard" className="space-y-6">
          {!isKnockoutSeries ? (
            <LeaderboardTable
              leaderboard={leaderboard}
              loading={loadingLeaderboard}
              seriesTitle={series.title}
              showExport={true}
              title={t('series.leaderboard')}
              description={t('leaderboard.description', 'Current rankings and statistics for this series')}
              hideRank={series.format === 'SERIES_FORMAT_LADDER'}
            />
          ) : bracket?.state === 'KNOCKOUT_BRACKET_STATE_COMPLETED' ? (
            <>
              {/* Only show podium if series is FORMALLY completed */}
              {series.state === 'SERIES_STATE_COMPLETED' ? (
                <>
                  {completedSeriesPodium ? (
                    <TournamentPodium
                      champion={completedSeriesPodium.champion}
                      runnerUp={completedSeriesPodium.runnerUp}
                      thirdPlace={completedSeriesPodium.thirdPlace}
                      seriesTitle={series.title}
                      completedAt={series.podium?.completedAt}
                    />
                  ) : (
                    <Alert>
                      <AlertTitle>{t('knockout.leaderboard.incompleteTitle', 'Tournament results unavailable')}</AlertTitle>
                      <AlertDescription>
                        {t(
                          'knockout.leaderboard.incompleteDescription',
                          'Final player details are still loading. Refresh the page to see the podium once all players are available.'
                        )}
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              ) : (
                <Alert>
                  <AlertTitle>{t('knockout.leaderboard.readyToCompleteTitle', 'Redo att avsluta')}</AlertTitle>
                  <AlertDescription>
                    {t(
                      'knockout.leaderboard.readyToCompleteDescription',
                      'Alla matcher är rapporterade. Klicka på "Avsluta serie" för att låsa resultatet och visa prispallplatser.'
                    )}
                  </AlertDescription>
                </Alert>
              )}
              <TournamentMatchHistory
                bracket={bracket}
                groupStage={groupStage}
                playersById={playersById}
              />
            </>
          ) : bracket?.state === 'KNOCKOUT_BRACKET_STATE_ACTIVE' ? (
            <>
              <Alert>
                <AlertTitle>{t('knockout.leaderboard.activeTitle', 'Tournament in progress')}</AlertTitle>
                <AlertDescription>
                  {t(
                    'knockout.leaderboard.activeDescription',
                    'Keep reporting match results. The podium will appear here as soon as the final is completed.'
                  )}
                </AlertDescription>
              </Alert>
              <CurrentBracketStandings bracket={bracket} playersById={playersById} />
            </>
          ) : groupStage?.state === 'GROUP_STAGE_STATE_ACTIVE' || groupStage?.state === 'GROUP_STAGE_STATE_COMPLETED' ? (
            <Alert>
              <AlertTitle>{t('knockout.leaderboard.groupStageTitle', 'Group stage underway')}</AlertTitle>
              <AlertDescription>
                {t(
                  'knockout.leaderboard.groupStageDescription',
                  'Finish the group phase and generate the bracket to unlock final standings and the podium.'
                )}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertTitle>{t('knockout.leaderboard.notStartedTitle', 'Tournament has not started yet')}</AlertTitle>
              <AlertDescription className="text-current">
                {t(
                  'knockout.leaderboard.notStartedDescription',
                  'Create the bracket from the overview tab to start the competition and track results here.'
                )}
              </AlertDescription>
            </Alert>
          )}
          </TabsContent>
        )}
      </Tabs>

      {!isKnockoutSeries && (
        <ReportMatchDialog
          open={showReportDialog}
          onOpenChange={setShowReportDialog}
          seriesId={series.id}
          clubId={series.visibility === 'SERIES_VISIBILITY_CLUB_ONLY' ? series.clubId : undefined}
          seriesStartDate={series.startsAt}
          seriesEndDate={series.endsAt}
          series={series}
          onMatchReported={handleMatchReported}
        />
      )}

      {includesGroupStage && (
        <ReportGroupStageMatchDialog
          open={showGroupReport}
          onOpenChange={handleGroupDialogOpenChange}
          seriesId={series.id}
          groupId={activeGroupId}
          match={activeGroupMatch}
          playersById={playersById}
          sport={series.sport}
          setsToPlay={series.setsToPlay}
          onReported={handleGroupStageMatchReported}
        />
      )}

      {isKnockoutSeries && bracket && (
        <ReportKnockoutMatchDialog
          bracketId={bracket.id}
          open={showKnockoutReport}
          onOpenChange={handleKnockoutDialogOpenChange}
          match={activeBracketMatch}
          playersById={playersById}
          sport={series.sport}
          setsToPlay={series.setsToPlay}
          onReported={handleKnockoutMatchReported}
        />
      )}

      {editingMatch && (
        <TournamentMatchEditDialog
          isOpen={showEditDialog}
          onClose={() => {
            setShowEditDialog(false)
            setEditingMatch(null)
          }}
          onMatchUpdated={handleMatchEdited}
          matchId={editingMatch.match.id}
          bracketId={!editingMatch.isGroupStage && bracket ? bracket.id : undefined}
          seriesId={editingMatch.isGroupStage ? series.id : undefined}
          groupId={editingMatch.groupId}
          currentScoreA={editingMatch.match.scoreA ?? 0}
          currentScoreB={editingMatch.match.scoreB ?? 0}
          playerAName={playersById[editingMatch.match.playerAId ?? '']?.displayName || 'Player A'}
          playerBName={playersById[editingMatch.match.playerBId ?? '']?.displayName || 'Player B'}
          isGroupStage={editingMatch.isGroupStage}
          sport={series.sport}
          setsToPlay={series.setsToPlay}
        />
      )}

      <SeriesRulesDialog
        open={showRulesDialog}
        onOpenChange={setShowRulesDialog}
        format={series.format}
        ladderRules={series.ladderRules}
        sport={series.sport}
      />

      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('series.complete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('series.complete_confirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={completing}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCompleteSeries} disabled={completing}>
              {completing ? t('common.loading') : t('common.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}